import { useState, useRef, useEffect } from "react";
import cx from "classnames";
import { Input } from "@repo/ping-design";
import type { RenderEditCellProps } from "react-data-grid";
import { useCellSave } from "../useCellSave";
import type { TopBuildingsListRow } from "../TopBuildingsList";

type Props = RenderEditCellProps<TopBuildingsListRow> & {
  onSave: (value: string | null) => Promise<boolean>;
};

export function TextCellEditor({ row, column, onSave, onClose }: Props) {
  const initial = String(row.values[column.key] ?? "");
  const [value, setValue] = useState(initial);
  const { status, runSave } = useCellSave();
  const wrapperRef = useRef<HTMLDivElement>(null);
  const valueRef = useRef(initial);
  const savedRef = useRef(false);

  const handleSave = async (): Promise<boolean> => {
    if (savedRef.current) return true;
    const trimmed = valueRef.current.trim();
    if (trimmed === initial) return true;
    savedRef.current = true;
    const ok = await runSave(() => onSave(trimmed || null));
    if (!ok) savedRef.current = false;
    return ok;
  };

  const unmountSaveRef = useRef(() => {});
  unmountSaveRef.current = () => {
    if (savedRef.current) return;
    const trimmed = valueRef.current.trim();
    if (trimmed === initial) return;
    savedRef.current = true;
    onSave(trimmed || null);
  };

  useEffect(() => () => unmountSaveRef.current(), []);

  return (
    <div
      ref={wrapperRef}
      className={cx("TopBuildingsList__CellEditorWrapper", {
        "TopBuildingsList__CellEditorWrapper--Error": status === "error",
      })}
      onBlur={async (e) => {
        if (wrapperRef.current?.contains(e.relatedTarget as Node)) return;
        const ok = await handleSave();
        if (ok) onClose(true);
      }}
    >
      <Input
        name={column.key}
        value={value}
        autoFocus
        style={{ minHeight: "20px" }}
        disabled={status === "saving"}
        onChange={(_name, v) => {
          setValue(String(v));
          valueRef.current = String(v);
        }}
        onKeyDown={(e) => {
          if (e.key === "Escape") {
            savedRef.current = true;
            onClose(false);
          }
          if (e.key === "Enter") {
            e.stopPropagation();
            handleSave().then((ok) => {
              if (ok) onClose(true);
            });
          }
        }}
      />
    </div>
  );
}
